import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '../../../base/base.component';
import { InputComponent } from '../../../core/controls/input.component';
import { RangeComponent } from '../../../core/controls/range.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { FieldContainerComponent } from '../../../core/fields/field-container.component';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { FilterSection, ICriteria } from '../edit-filter.component';
import { ValidatorsEx } from '../../../../validators/validators-ex';

@Component({
  selector: 'app-date-criteria',
  imports: [
    CommonModule,
    SelectComponent,
    RangeComponent,
    InputComponent,
    FieldContainerComponent,
  ],
  template: `
    <ng-template #dateTemplate let-item>
      <app-field-container
        [label]="item.label"
        labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap max-sm:tw-pb-1"
        valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0"
      >
        <ng-template #value>
          <div
            class="tw-flex tw-flex-col md:tw-flex-row tw-flex-full tw-items-start tw-gap-2"
          >
            <app-select
              [control]="item.controls.operator"
              optionLabel="description"
              optionValue="value"
              label="Operator"
              [virtualScroll]="false"
              [showClear]="false"
              [filter]="false"
              [options]="options"
              class="tw-w-full md:tw-min-w-[175px]"
              [showHelpText]="false"
            ></app-select>
            @if (item.controls.operator.value === 'date_range') {
              <div class="tw-flex tw-w-full md:tw-max-w-[300px]">
                <app-range
                  [control]="item.controls.from"
                  [toControl]="item.controls.to"
                  [showHelpText]="true"
                  [screenReaderLabel]="item.label"
                  [maxSpanDays]="null"
                />
              </div>
            } @else if (
              item.controls.operator.value === 'between_the_previous'
            ) {
              <div class="tw-flex tw-flex-row tw-w-full md:tw-max-w-[195px]">
                <app-input
                  type="Integer"
                  label="From"
                  [control]="item.controls.rangeOne"
                  [showHelpText]="true"
                  [min]="0"
                />
                <app-input
                  type="Integer"
                  label="To"
                  [control]="item.controls.rangeTwo"
                  [showHelpText]="true"
                  [min]="0"
                />
              </div>
            } @else if (
              item.controls.operator.value === 'within_the_last' ||
              item.controls.operator.value === 'not_within_the_last'
            ) {
              <div class="tw-flex tw-flex-row tw-w-full md:tw-max-w-[100px]">
                <app-input
                  type="Integer"
                  label="From"
                  [control]="item.controls.rangeOne"
                  [showHelpText]="true"
                  [min]="0"
                />
              </div>
            }
            @if (item.controls.operator.value !== 'date_range') {
              <app-select
                [control]="item.controls.timePeriod"
                label="Time Period"
                optionLabel="description"
                optionValue="value"
                [virtualScroll]="false"
                [showClear]="false"
                [filter]="false"
                [options]="timePeriodOptions"
                class="tw-w-full md:tw-w-[150px]"
                [showHelpText]="true"
              ></app-select>
            }
          </div>
        </ng-template>
      </app-field-container>
    </ng-template>
    <div>
      <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full">
        <div
          class="tw-w-full tw-italic tw-pb-4 tw-mb-2 tw-text-gray-600 tw-max-w-[700px]"
        >
          Note: It is recommended that a filter with relative dates be used when
          requesting a report with recurring schedule option. The date ranges
          section will be disabled on filters that are used on active scheduled
          reports.
        </div>
        @for (dateCriteria of dateCriterias; track dateCriteria) {
          <ng-container
            *ngTemplateOutlet="
              dateTemplate;
              context: { $implicit: dateCriteria }
            "
          ></ng-container>
        }
      </div>
    </div>
  `,
  styles: [],
})
export class DateCriteriaComponent
  extends BaseComponent
  implements OnInit, ICriteria
{
  @Input({ required: true }) section: FilterSection;

  fg = new FormGroup({
    lossDateOperator: new FormControl<string>('date_range'),
    lossDateFrom: new FormControl<Date | null>(null),
    lossDateTo: new FormControl<Date | null>(null),
    lossDateRangeOne: new FormControl<number | null>(null),
    lossDateRangeTwo: new FormControl<number | null>(null),
    lossDateTimePeriod: new FormControl<string | null>(null),

    reportedToClientOperator: new FormControl<string>('date_range'),
    reportedToClientFrom: new FormControl<Date | null>(null),
    reportedToClientTo: new FormControl<Date | null>(null),
    reportedToClientRangeOne: new FormControl<number | null>(null),
    reportedToClientRangeTwo: new FormControl<number | null>(null),
    reportedToClientTimePeriod: new FormControl<string | null>(null),

    reportedToSedgwickOperator: new FormControl<string>('date_range'),
    reportedToSedgwickFrom: new FormControl<Date | null>(null),
    reportedToSedgwickTo: new FormControl<Date | null>(null),
    reportedToSedgwickRangeOne: new FormControl<number | null>(null),
    reportedToSedgwickRangeTwo: new FormControl<number | null>(null),
    reportedToSedgwickTimePeriod: new FormControl<string | null>(null),

    claimOpenedOperator: new FormControl<string>('date_range'),
    claimOpenedFrom: new FormControl<Date | null>(null),
    claimOpenedTo: new FormControl<Date | null>(null),
    claimOpenedRangeOne: new FormControl<number | null>(null),
    claimOpenedRangeTwo: new FormControl<number | null>(null),
    claimOpenedTimePeriod: new FormControl<string | null>(null),

    claimClosedOperator: new FormControl<string>('date_range'),
    claimClosedFrom: new FormControl<Date | null>(null),
    claimClosedTo: new FormControl<Date | null>(null),
    claimClosedRangeOne: new FormControl<number | null>(null),
    claimClosedRangeTwo: new FormControl<number | null>(null),
    claimClosedTimePeriod: new FormControl<string | null>(null),

    reopenedOperator: new FormControl<string>('date_range'),
    reopenedFrom: new FormControl<Date | null>(null),
    reopenedTo: new FormControl<Date | null>(null),
    reopenedRangeOne: new FormControl<number | null>(null),
    reopenedRangeTwo: new FormControl<number | null>(null),
    reopenedTimePeriod: new FormControl<string | null>(null),

    claimLastUpdateOperator: new FormControl<string>('date_range'),
    claimLastUpdateFrom: new FormControl<Date | null>(null),
    claimLastUpdateTo: new FormControl<Date | null>(null),
    claimLastUpdateRangeOne: new FormControl<number | null>(null),
    claimLastUpdateRangeTwo: new FormControl<number | null>(null),
    claimLastUpdateTimePeriod: new FormControl<string | null>(null),

    lastFinancialUpdateOperator: new FormControl<string>('date_range'),
    lastFinancialUpdateFrom: new FormControl<Date | null>(null),
    lastFinancialUpdateTo: new FormControl<Date | null>(null),
    lastFinancialUpdateRangeOne: new FormControl<number | null>(null),
    lastFinancialUpdateRangeTwo: new FormControl<number | null>(null),
    lastFinancialUpdateTimePeriod: new FormControl<string | null>(null),
  });

  dateCriterias: DateCriteria[] = [
    {
      label: 'Date of loss',
      column: 'DATE_OF_LOSS',
      tableAlias: 'claim',
      controls: {
        operator: this.fg.controls.lossDateOperator,
        from: this.fg.controls.lossDateFrom,
        to: this.fg.controls.lossDateTo,
        rangeOne: this.fg.controls.lossDateRangeOne,
        rangeTwo: this.fg.controls.lossDateRangeTwo,
        timePeriod: this.fg.controls.lossDateTimePeriod,
      },
    },
    {
      label: 'Date reported to client',
      column: 'DATE_REPORTED_TO_CLIENT',
      tableAlias: 'claim',
      controls: {
        operator: this.fg.controls.reportedToClientOperator,
        from: this.fg.controls.reportedToClientFrom,
        to: this.fg.controls.reportedToClientTo,
        rangeOne: this.fg.controls.reportedToClientRangeOne,
        rangeTwo: this.fg.controls.reportedToClientRangeTwo,
        timePeriod: this.fg.controls.reportedToClientTimePeriod,
      },
    },
    {
      label: 'Date reported to Sedgwick',
      column: 'DATE_REPORTED_TO_CMS',
      tableAlias: 'claim',
      controls: {
        operator: this.fg.controls.reportedToSedgwickOperator,
        from: this.fg.controls.reportedToSedgwickFrom,
        to: this.fg.controls.reportedToSedgwickTo,
        rangeOne: this.fg.controls.reportedToSedgwickRangeOne,
        rangeTwo: this.fg.controls.reportedToSedgwickRangeTwo,
        timePeriod: this.fg.controls.reportedToSedgwickTimePeriod,
      },
    },
    {
      label: 'Date claim opened',
      column: 'DATE_CLAIM_OPENED',
      tableAlias: 'claim',
      controls: {
        operator: this.fg.controls.claimOpenedOperator,
        from: this.fg.controls.claimOpenedFrom,
        to: this.fg.controls.claimOpenedTo,
        rangeOne: this.fg.controls.claimOpenedRangeOne,
        rangeTwo: this.fg.controls.claimOpenedRangeTwo,
        timePeriod: this.fg.controls.claimOpenedTimePeriod,
      },
    },
    {
      label: 'Date claim closed',
      column: 'FILTER_DATE_CLOSE',
      tableAlias: 'filter_close_date',
      controls: {
        operator: this.fg.controls.claimClosedOperator,
        from: this.fg.controls.claimClosedFrom,
        to: this.fg.controls.claimClosedTo,
        rangeOne: this.fg.controls.claimClosedRangeOne,
        rangeTwo: this.fg.controls.claimClosedRangeTwo,
        timePeriod: this.fg.controls.claimClosedTimePeriod,
      },
    },
    {
      label: 'Date claim reopened',
      column: 'FILTER_DATE_REOPEN',
      tableAlias: 'filter_reopen_date',
      controls: {
        operator: this.fg.controls.reopenedOperator,
        from: this.fg.controls.reopenedFrom,
        to: this.fg.controls.reopenedTo,
        rangeOne: this.fg.controls.reopenedRangeOne,
        rangeTwo: this.fg.controls.reopenedRangeTwo,
        timePeriod: this.fg.controls.reopenedTimePeriod,
      },
    },
    {
      label: 'Claim last update',
      column: 'DATE_UPDATED',
      tableAlias: 'claim',
      controls: {
        operator: this.fg.controls.claimLastUpdateOperator,
        from: this.fg.controls.claimLastUpdateFrom,
        to: this.fg.controls.claimLastUpdateTo,
        rangeOne: this.fg.controls.claimLastUpdateRangeOne,
        rangeTwo: this.fg.controls.claimLastUpdateRangeTwo,
        timePeriod: this.fg.controls.claimLastUpdateTimePeriod,
      },
    },
    {
      label: 'Last financial update',
      column: 'DATE_FINANCIALS_UPDATED',
      tableAlias: 'claim',
      controls: {
        operator: this.fg.controls.lastFinancialUpdateOperator,
        from: this.fg.controls.lastFinancialUpdateFrom,
        to: this.fg.controls.lastFinancialUpdateTo,
        rangeOne: this.fg.controls.lastFinancialUpdateRangeOne,
        rangeTwo: this.fg.controls.lastFinancialUpdateRangeTwo,
        timePeriod: this.fg.controls.lastFinancialUpdateTimePeriod,
      },
    },
  ];

  options = [
    { description: 'Date range', value: 'date_range' },
    { description: 'Within the last', value: 'within_the_last' },
    { description: 'Not within the last', value: 'not_within_the_last' },
    { description: 'Between the previous', value: 'between_the_previous' },
  ];

  timePeriodOptions = [
    { description: 'Day(s)', value: 'days' },
    { description: 'Week(s)', value: 'weeks' },
    { description: 'Month(s)', value: 'months' },
    { description: 'Year(s)', value: 'years' },
  ];

  setDatesFromCriteria(criteria: FilterCriteria[]) {
    this.dateCriterias.forEach((date) => {
      const found = criteria.find(
        (c) => c.tableAlias === date.tableAlias && c.column === date.column,
      );

      if (!found) return;

      const op = found.operator;
      const value = found.values?.[0] || '';

      if (op === 'BETWEEN') {
        const [fromStr, toStr] = value.split('|');
        date.controls.operator.setValue('date_range');
        date.controls.from.setValue(this.parseDate(fromStr));
        date.controls.to.setValue(this.parseDate(toStr));
      } else if (op.startsWith('WITHIN_LAST_')) {
        const period = this.extractTimePeriod(op);
        date.controls.operator.setValue('within_the_last');
        date.controls.rangeOne.setValue(Number(value));
        date.controls.timePeriod.setValue(period);
      } else if (op.startsWith('NOT_WITHIN_LAST_')) {
        const period = this.extractTimePeriod(op);
        date.controls.operator.setValue('not_within_the_last');
        date.controls.rangeOne.setValue(Number(value));
        date.controls.timePeriod.setValue(period);
      } else if (op.startsWith('BETWEEN_THE_PREVIOUS_')) {
        const period = this.extractTimePeriod(op);
        const [rangeOne, rangeTwo] = value.split('|');
        date.controls.operator.setValue('between_the_previous');
        date.controls.rangeOne.setValue(Number(rangeOne));
        date.controls.rangeTwo.setValue(Number(rangeTwo));
        date.controls.timePeriod.setValue(period);
      }
    });
  }

  private parseDate(dateStr: string): Date | null {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    return new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
  }

  private extractTimePeriod(operator: string): string {
    const upper = operator.replace(/_OF$/, '');
    const segments = upper.split('_');
    const period = segments[segments.length - 1].toLowerCase();
    return period;
  }

  async ngOnInit() {
    this.section.criteria = this;

    if (this.section.values?.length) {
      this.setDatesFromCriteria(this.section.values);
    }

    for (const item of this.dateCriterias) {
      this.dispose.subs = item.controls.operator.valueChanges.subscribe(
        (value) => {
          this.resetFieldsForOperator(item, value);
          this.updateNumericGroupValidation(item);
          this.emitCount();
        },
      );

      this.setupDatePairValidation(item.controls.from, item.controls.to);

      this.dispose.subs = item.controls.rangeOne.valueChanges.subscribe(() => {
        this.updateNumericGroupValidation(item);
        item.controls.rangeTwo.updateValueAndValidity({ emitEvent: false });
        this.emitCount();
      });
      this.dispose.subs = item.controls.rangeTwo.valueChanges.subscribe(() => {
        this.updateNumericGroupValidation(item);
        item.controls.rangeOne.updateValueAndValidity({ emitEvent: false });
        this.emitCount();
      });
      this.dispose.subs = item.controls.timePeriod.valueChanges.subscribe(
        () => {
          this.updateNumericGroupValidation(item);
          this.emitCount();
        },
      );
    }
  }

  private resetFieldsForOperator(item: DateCriteria, operator: string): void {
    item.controls.from.clearValidators();
    item.controls.to.clearValidators();
    item.controls.from.setValue(null, { emitEvent: false });
    item.controls.to.setValue(null, { emitEvent: false });
    item.controls.rangeOne.setValue(null, { emitEvent: false });
    item.controls.rangeTwo.setValue(null, { emitEvent: false });
    item.controls.timePeriod.setValue(null, { emitEvent: false });
  }

  private setupDatePairValidation(
    from: FormControl<Date | null>,
    to: FormControl<Date | null>,
  ): void {
    this.dispose.subs = from.valueChanges.subscribe((value) => {
      if (value) {
        to.addValidators(Validators.required);
        to.markAsTouched();
      } else if (!to.value) {
        to.removeValidators(Validators.required);
      }
      to.updateValueAndValidity({ emitEvent: false });
      this.emitCount();
    });

    this.dispose.subs = to.valueChanges.subscribe((value) => {
      if (value) {
        from.addValidators(Validators.required);
        from.markAsTouched();
      } else if (!from.value) {
        from.removeValidators(Validators.required);
      }
      from.updateValueAndValidity({ emitEvent: false });
      this.emitCount();
    });
  }

  private updateNumericGroupValidation(item: DateCriteria): void {
    item.controls.rangeOne.clearValidators();
    item.controls.rangeTwo.clearValidators();
    item.controls.timePeriod.clearValidators();

    const op = item.controls.operator.value;

    if (op === 'within_the_last' || op === 'not_within_the_last') {
      const hasAnyValue =
        item.controls.rangeOne.value !== null ||
        item.controls.timePeriod.value !== null;

      if (hasAnyValue) {
        item.controls.rangeOne.addValidators(Validators.required);
        item.controls.timePeriod.addValidators(Validators.required);
        item.controls.rangeOne.markAsTouched();
        item.controls.timePeriod.markAsTouched();
      } else {
        item.controls.rangeOne.removeValidators(Validators.required);
        item.controls.timePeriod.removeValidators(Validators.required);
      }

      item.controls.rangeOne.updateValueAndValidity({ emitEvent: false });
      item.controls.timePeriod.updateValueAndValidity({ emitEvent: false });
    } else if (op === 'between_the_previous') {
      const hasAnyValue =
        item.controls.rangeOne.value !== null ||
        item.controls.rangeTwo.value !== null ||
        item.controls.timePeriod.value !== null;

      if (hasAnyValue) {
        item.controls.rangeOne.addValidators(Validators.required);
        item.controls.rangeTwo.addValidators(Validators.required);
        item.controls.timePeriod.addValidators(Validators.required);
        item.controls.rangeOne.markAsTouched();
        item.controls.rangeTwo.markAsTouched();
        item.controls.timePeriod.markAsTouched();
      } else {
        item.controls.rangeOne.removeValidators(Validators.required);
        item.controls.rangeTwo.removeValidators(Validators.required);
        item.controls.timePeriod.removeValidators(Validators.required);
      }

      item.controls.rangeOne.addValidators([
        ValidatorsEx.integerLessThan(
          item.controls.rangeTwo,
          false,
          'From value must be less than To value',
        ),
      ]);
      item.controls.rangeTwo.addValidators([
        ValidatorsEx.integerGreaterThan(
          item.controls.rangeOne,
          false,
          'To value must be greater than From value',
        ),
      ]);

      item.controls.rangeOne.updateValueAndValidity({ emitEvent: false });
      item.controls.rangeTwo.updateValueAndValidity({ emitEvent: false });
      item.controls.timePeriod.updateValueAndValidity({ emitEvent: false });
    }
  }

  private isComplete(criteria: DateCriteria): boolean {
    const op = criteria.controls.operator.value;

    if (op === 'date_range') {
      return (
        criteria.controls.from.value !== null &&
        criteria.controls.to.value !== null &&
        criteria.controls.from.valid &&
        criteria.controls.to.valid
      );
    } else if (op === 'between_the_previous') {
      return (
        criteria.controls.rangeOne.value !== null &&
        criteria.controls.rangeTwo.value !== null &&
        criteria.controls.timePeriod.value !== null &&
        criteria.controls.rangeOne.valid &&
        criteria.controls.rangeTwo.valid
      );
    } else if (op === 'within_the_last' || op === 'not_within_the_last') {
      return (
        criteria.controls.rangeOne.value !== null &&
        criteria.controls.timePeriod.value !== null &&
        criteria.controls.rangeOne.valid
      );
    }

    return false;
  }

  private emitCount(): void {
    this.section.count.set(
      this.dateCriterias.filter((c) => this.isComplete(c)).length,
    );
  }

  valid(): boolean {
    return this.fg.valid;
  }

  getCriteria(): FilterCriteria[] {
    const formatDate = (d: Date) =>
      `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`;

    return this.dateCriterias
      .filter((dateCriteria) => this.isComplete(dateCriteria))
      .map((dateCriteria) => {
        const op = dateCriteria.controls.operator.value;
        let operator = '';
        let values: string[] = [];

        if (op === 'date_range') {
          operator = 'BETWEEN';
          const fromDate = dateCriteria.controls.from.value;
          const toDate = dateCriteria.controls.to.value;
          values = [`${formatDate(fromDate)}|${formatDate(toDate)}`];
        } else if (op === 'within_the_last' || op === 'not_within_the_last') {
          const period = dateCriteria.controls.timePeriod.value;
          const prefix =
            op === 'within_the_last' ? 'WITHIN_LAST' : 'NOT_WITHIN_LAST';
          operator = `${prefix}_${period.toUpperCase()}_OF`;
          values = [`${dateCriteria.controls.rangeOne.value}`];
        } else if (op === 'between_the_previous') {
          const period = dateCriteria.controls.timePeriod.value;
          operator = `BETWEEN_THE_PREVIOUS_${period.toUpperCase()}_OF`;
          values = [
            `${dateCriteria.controls.rangeOne.value}|${dateCriteria.controls.rangeTwo.value}`,
          ];
        }

        return {
          tableAlias: dateCriteria.tableAlias,
          column: dateCriteria.column,
          displayName: dateCriteria.label,
          operator: operator,
          values: values,
          lineOfBusiness: '',
          dataType: 'DATE',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Date',
          filterSubsection: '',
        };
      });
  }
}

interface DateCriteria {
  label: string;
  column: string;
  tableAlias: string;
  controls: {
    operator: FormControl<string>;
    from: FormControl<Date | null>;
    to: FormControl<Date | null>;
    rangeOne: FormControl<number | null>;
    rangeTwo: FormControl<number | null>;
    timePeriod: FormControl<string | null>;
  };
}
